/**
 * Coming Soon page for professional plans (Organizer, Mover Pro, Enterprise)
 * with wishlist signup functionality
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Alert,
  Card,
  CardContent,
  Chip,
  AppBar,
  Toolbar,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  LocalShipping as LocalShippingIcon,
  Domain as DomainIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useAppNavigation } from '../hooks/useAppNavigation';
import wishlistService from '../services/wishlistService';

const ComingSoon = () => {
  const navigate = useAppNavigation();
  const [email, setEmail] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const professionalPlans = [
    {
      id: 'organizer',
      name: 'Organizer',
      price: '$29/mo',
      description: 'For professional organizers',
      icon: <BusinessIcon sx={{ fontSize: 40 }} />,
      features: [
        'Manage 10+ clients',
        'Shared dashboard',
        'Bulk import/export',
        'Branded reports',
        'Client billing integration'
      ]
    },
    {
      id: 'mover_pro',
      name: 'Mover Pro',
      price: '$59/mo',
      description: 'For moving companies',
      icon: <LocalShippingIcon sx={{ fontSize: 40 }} />,
      features: [
        'Unlimited clients',
        'Moving timeline tracking',
        'Insurance documentation',
        'QR code labels',
        'White-label solution'
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 'Custom',
      description: 'For large organizations',
      icon: <DomainIcon sx={{ fontSize: 40 }} />,
      features: [
        'Custom integrations',
        'SSO authentication',
        'Advanced analytics',
        'Dedicated support',
        'On-premise deployment'
      ]
    }
  ];

  const handleWishlistSignup = async (e) => {
    e.preventDefault();
    
    if (!email || !selectedPlan) {
      setError('Please enter your email and select a plan');
      return;
    }

    setIsSubmitting(true);
    setError('');
    
    try {
      const result = await wishlistService.subscribe(email, selectedPlan);
      
      if (result.success) {
        const planName = professionalPlans.find(p => p.id === selectedPlan)?.name;
        setMessage(`Thanks! We'll notify you at ${email} when ${planName} is available.`);
        setEmail('');
        setSelectedPlan('');
        
        // Clear message after 5 seconds
        setTimeout(() => setMessage(''), 5000);
      } else {
        setError(result.error || 'Failed to sign up for wishlist. Please try again.');
      }
    } catch (err) {
      console.error('Wishlist signup error:', err);
      setError('Failed to sign up for wishlist. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate.to.SUBSCRIPTION()}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Professional Plans - Wishlist
          </Typography>
        </Toolbar>
      </AppBar>

      <Container component="main" sx={{ flexGrow: 1, py: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" gutterBottom>
            Professional Plans Wishlist
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            Join our wishlist for professional organizers, moving companies, and enterprises
          </Typography>
          <Chip 
            label="In Development" 
            color="primary" 
            variant="outlined" 
            sx={{ fontSize: '1rem', py: 1, px: 2 }}
          />
        </Box>

        {/* Wishlist Signup - Moved to top */}
        <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto', mb: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <EmailIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Join the Wishlist
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Be the first to know when professional plans are available
            </Typography>
          </Box>

          {message && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {message}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleWishlistSignup}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{ mb: 3 }}
              placeholder="your@email.com"
            />

            <Typography variant="body2" sx={{ mb: 2 }}>
              {selectedPlan ? 
                `You'll be notified when ${professionalPlans.find(p => p.id === selectedPlan)?.name} is available` :
                'Please select a plan below to continue'
              }
            </Typography>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={!email || !selectedPlan || isSubmitting}
              sx={{ py: 1.5 }}
            >
              {isSubmitting ? 'Signing Up...' : 'Subscribe to Wishlist'}
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            We'll only email you about the plan you selected. No spam, unsubscribe anytime.
          </Typography>
        </Paper>

        {/* Professional Plans */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {professionalPlans.map((plan) => (
            <Grid item xs={12} md={4} key={plan.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  cursor: 'pointer',
                  border: selectedPlan === plan.id ? 2 : 1,
                  borderColor: selectedPlan === plan.id ? 'primary.main' : 'divider',
                  '&:hover': {
                    boxShadow: 3
                  }
                }}
                onClick={() => setSelectedPlan(plan.id)}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {plan.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom>
                    {plan.name}
                  </Typography>
                  <Typography variant="h4" color="primary" gutterBottom>
                    {plan.price}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {plan.description}
                  </Typography>
                  
                  <Box sx={{ textAlign: 'left' }}>
                    {plan.features.map((feature, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 16, mr: 1, color: 'success.main' }} />
                        {feature}
                      </Typography>
                    ))}
                  </Box>
                  
                  {selectedPlan === plan.id && (
                    <Chip 
                      label="Selected" 
                      color="primary" 
                      size="small" 
                      sx={{ mt: 2 }}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Timeline */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Expected Timeline
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Professional plans are currently in development. We expect to launch in Q2 2025.
            Join our wishlist to get early access and special launch pricing.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ComingSoon;
